import React, { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { House, Book, History, UserRound, type LucideIcon } from 'lucide-react';
import { cn } from 'lib/utils';
import LogoMark from 'components/global/LogoMark';
import { PortalAccountMenu } from './PortalAccountMenu';
import { PortalBalancePill } from './PortalBalancePill';
import { PortalTestsMenu } from './PortalTestsMenu';
import { portalNavLinkClass } from './navLinkClass';
import { TelegramStartParamHandler } from 'components/TelegramStartParamHandler';
import { TelegramBackButton } from 'components/TelegramBackButton';
import { useAuthContext } from 'modules/auth/context';
import { usePortalSubscription } from 'modules/portal-plans/usePortalSubscription';
import { PlanCheckoutModal } from 'components/portal/PlanCheckoutModal';
import { IS_IO } from 'lib/site';

/** examing.io has no marketplace: no home feed and no tests catalog. */
const IO_HIDDEN = new Set(['/portal', '/portal/tests', '/portal/words']);

/** Desktop top-bar destinations; "Testlar" renders as the category menu. */
const NAV_ITEMS: { to: string; end?: boolean; labelKey: string }[] = [
  { to: '/portal', end: true, labelKey: 'portal.home' },
  { to: '/portal/tests', labelKey: 'portal.tests' },
  { to: '/portal/words', labelKey: 'portal.words' },
  { to: '/portal/settings/history', labelKey: 'portal.history' },
].filter((item) => !IS_IO || !IO_HIDDEN.has(item.to));

const isHistory = (path: string) => path.startsWith('/portal/settings/history');

/** Phone tab bar. History lives under settings, so "Hisob" is current on every other settings page. */
const TABS: { key: string; to: string; labelKey: string; icon: LucideIcon; isActive: (path: string) => boolean }[] = [
  { key: 'home', to: '/portal', labelKey: 'portal.home', icon: House, isActive: (p) => p === '/portal' || p === '/portal/' },
  { key: 'tests', to: '/portal/tests', labelKey: 'portal.tests', icon: Book, isActive: (p) => p.startsWith('/portal/tests') },
  { key: 'history', to: '/portal/settings/history', labelKey: 'portal.history', icon: History, isActive: isHistory },
  {
    key: 'account',
    to: '/portal/settings',
    labelKey: 'portal.account',
    icon: UserRound,
    isActive: (p) => p.startsWith('/portal/settings') && !isHistory(p),
  },
].filter((tab) => !IS_IO || !IO_HIDDEN.has(tab.to));

const pillFocus = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** Who lands here is decided by the router's role-based experience switch (see isCandidateExperience). */
export const CandidatePortalLayout: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { state } = useAuthContext();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const { data: subscription } = usePortalSubscription();
  const hasActiveSubscription = Boolean(
    subscription && new Date(subscription.periodEnd).getTime() > Date.now(),
  );
  const activePlanName = hasActiveSubscription
    ? subscription?.planName
    : state.organization?.tier &&
      state.organization.tier !== 'FREE' &&
      state.organization.tier !== 'EXPIRED'
      ? state.organization.tier
      : null;

  return (
    <div data-surface="portal" className="min-h-screen bg-muted/30 text-foreground">
      {/* Telegram deep link (startapp=<code>) — renders nothing outside Telegram */}
      <TelegramStartParamHandler />
      {/* Native Telegram back chevron, wired to the router. Browser: no-op. */}
      <TelegramBackButton />
      <header className="sticky top-0 z-30 border-b border-border bg-card">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-7 px-4 md:h-16 md:px-6">
          <Link
            to="/portal"
            className={cn('flex shrink-0 items-center gap-[9px] rounded-md text-foreground md:gap-2.5', pillFocus)}
          >
            {/* The mark's alt text names the link; the wordmark would only repeat it. */}
            <LogoMark size={28} className="rounded-[8px]" />
            <span aria-hidden="true" className="font-display text-base font-extrabold tracking-[-0.02em] md:text-[17px]">
              Examing
            </span>
          </Link>
          <nav aria-label={t('portal.navigation')} className="hidden items-center gap-1 md:flex">
            {NAV_ITEMS.map(({ to, end, labelKey }) =>
              to === '/portal/tests' ? (
                <PortalTestsMenu key={to} />
              ) : (
                <NavLink key={to} to={to} end={end} className={({ isActive }) => portalNavLinkClass(isActive)}>
                  {t(labelKey)}
                </NavLink>
              ),
            )}
          </nav>
          <div className="ml-auto flex items-center gap-2 md:gap-2.5">
            {IS_IO ? null : activePlanName ? (
              // Ink fill makes the live subscription the header's one dark focal point and
              // keeps it apart from the lime upgrade CTA below. The lime dot is the accent.
              // Dark theme: the ink fill would flip to near-white and wash the dot out, so it
              // becomes a card-toned pill with a lime ring instead.
              <button
                type="button"
                onClick={() => setCheckoutOpen(true)}
                className={cn(
                  'relative inline-flex h-7 shrink-0 items-center gap-2 overflow-hidden rounded-full bg-foreground pl-2.5 pr-3 text-xs font-semibold text-background shadow-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-px hover:shadow-md active:translate-y-0 md:h-8 md:pl-3 md:pr-3.5 md:text-[12.5px]',
                  'dark:bg-card dark:text-foreground dark:ring-1 dark:ring-inset dark:ring-primary/60',
                  pillFocus,
                  'focus-visible:ring-offset-1 focus-visible:ring-offset-card',
                )}
              >
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.28)] md:h-[7px] md:w-[7px]"
                />
                <span className="md:hidden">{activePlanName}</span>
                <span className="hidden md:inline">{t('portal_nav_plan_pill', { plan: activePlanName })}</span>
                {/* One sheen sweep on hover; compositor-only, dropped for reduced motion.
                    Arbitrary variant, not group-hover: the web build drops group-* variants. */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 -skew-x-12 bg-background/25 [button:hover_&]:translate-x-[600%] [button:hover_&]:transition-transform [button:hover_&]:duration-700 [button:hover_&]:ease-out motion-reduce:hidden"
                />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setCheckoutOpen(true)}
                className={cn(
                  'inline-flex h-7 shrink-0 items-center rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 md:h-8 md:px-3.5 md:text-[12.5px]',
                  pillFocus,
                )}
              >
                {t('upgrade')}
              </button>
            )}
            {!IS_IO && <PortalBalancePill />}
            <PortalAccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl overflow-x-clip px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-5 md:px-6 md:pb-12 md:pt-9">
        <Outlet />
      </main>

      {/* Phone tab bar (< 768px): the current tab is ink with a short lime bar on top. */}
      <nav
        aria-label={t('portal.navigation')}
        className={cn(
          'fixed inset-x-0 bottom-0 z-30 grid border-t border-border bg-card md:hidden',
          TABS.length === 4 ? 'grid-cols-4' : 'grid-cols-2',
        )}
        style={{ height: 'calc(64px + env(safe-area-inset-bottom))', paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {TABS.map(({ key, to, labelKey, icon: Icon, isActive }) => {
          const active = isActive(pathname);
          return (
            <Link
              key={key}
              to={to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'relative flex min-w-0 flex-col items-center justify-center gap-[3px] text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                active ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground',
              )}
            >
              {active && (
                <span aria-hidden="true" className="absolute left-1/2 top-0 h-[3px] w-7 -translate-x-1/2 rounded-b-[3px] bg-primary" />
              )}
              <Icon size={21} strokeWidth={active ? 2.25 : 1.75} aria-hidden="true" />
              <span className="max-w-full truncate px-0.5">{t(labelKey)}</span>
            </Link>
          );
        })}
      </nav>

      {!IS_IO && <PlanCheckoutModal open={checkoutOpen} onOpenChange={setCheckoutOpen} subscription={subscription} />}
    </div>
  );
};

export default CandidatePortalLayout;
